// ============================================================================
// Design-system primitives for the CloudCost.dc.html reskin.
// Pure presentational components built from tokens — no data/logic.
// ============================================================================
import React from 'react';
import { T, TONES, Tone } from '@/theme/tokens';
import { DsIcon, DsIconName } from './icons';

export { DsIcon } from './icons';
export type { DsIconName } from './icons';

// ---- Pill / status tag -----------------------------------------------------
export function Pill({
  tone = 'neutral',
  children,
  style,
}: {
  tone?: Tone;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const t = TONES[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 999,
        color: t.color,
        background: t.tint,
        whiteSpace: 'nowrap',
        ...style,
      }}>
      {children}
    </span>
  );
}

// ---- Stat card (icon square + value + label) -------------------------------
export function StatCard({
  icon,
  value,
  label,
  tone = 'primary',
  iconColor,
  iconTint,
}: {
  icon: DsIconName;
  value: React.ReactNode;
  label: React.ReactNode;
  tone?: Tone;
  // Optional icon-chip colour override (e.g. bright warn #F28E2C on #FDEFDE)
  // without altering the shared TONES map used by pills.
  iconColor?: string;
  iconTint?: string;
}) {
  const t = TONES[tone];
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: 18,
        boxShadow: T.cardShadow,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: iconTint ?? t.tint,
          color: iconColor ?? t.color,
          flex: 'none',
        }}>
        <DsIcon name={icon} size={20} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 900,
            letterSpacing: '-.6px',
            color: T.text,
            lineHeight: 1,
          }}>
          {value}
        </div>
        <div style={{ fontSize: 12.5, color: T.textFaint, fontWeight: 700, marginTop: 4 }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// ---- KPI card (label + icon chip, big value, delta pill + sub) -------------
export function KpiCard({
  label,
  value,
  icon,
  tone = 'primary',
  delta,
  deltaTone = 'success',
  sub,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  icon: DsIconName;
  tone?: Tone;
  delta?: React.ReactNode;
  deltaTone?: Tone;
  sub?: React.ReactNode;
}) {
  const t = TONES[tone];
  const d = TONES[deltaTone];
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: '18px 18px 16px',
        boxShadow: T.cardShadow,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textFaint, letterSpacing: '.2px' }}>
          {label}
        </span>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: t.tint,
            color: t.color,
          }}>
          <DsIcon name={icon} size={17} />
        </span>
      </div>
      <div
        style={{ fontSize: 27, fontWeight: 900, letterSpacing: '-.8px', color: T.text, lineHeight: 1 }}>
        {value}
      </div>
      {(delta || sub) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9 }}>
          {delta != null && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: d.color,
                background: d.tint,
                padding: '2px 7px',
                borderRadius: 6,
              }}>
              {delta}
            </span>
          )}
          {sub != null && <span style={{ fontSize: 12, color: T.textSoft }}>{sub}</span>}
        </div>
      )}
    </div>
  );
}

// ---- Responsive grid row for stat/kpi cards --------------------------------
export function StatRow({
  columns = 4,
  children,
  style,
}: {
  columns?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className="cc-stat-row"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 16,
        marginBottom: 20,
        ...style,
      }}>
      {children}
    </div>
  );
}

// ---- Section card (white panel with icon+title+subtitle header) ------------
export function SectionCard({
  icon,
  iconTone = 'primary',
  title,
  subtitle,
  right,
  noBodyPad,
  children,
  style,
  bodyStyle,
}: {
  icon?: DsIconName;
  iconTone?: Tone;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  noBodyPad?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
}) {
  const t = TONES[iconTone];
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        boxShadow: T.cardShadow,
        overflow: 'hidden',
        ...style,
      }}>
      {(title || right) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            padding: '16px 20px',
            borderBottom: `1px solid ${T.divider}`,
            flexWrap: 'wrap',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {icon && (
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: t.tint,
                  color: t.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <DsIcon name={icon} size={18} />
              </span>
            )}
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: T.text }}>{title}</div>
              {subtitle && (
                <div style={{ fontSize: 12.5, color: T.textFaint2 }}>{subtitle}</div>
              )}
            </div>
          </div>
          {right}
        </div>
      )}
      <div style={{ padding: noBodyPad ? 0 : '18px 20px', ...bodyStyle }}>{children}</div>
    </div>
  );
}
